<template>
  <form>
    <FormHeader :title="title" @cancel="onCancel" @submit="onSubmit"/>
    <div class="space-y-12 mt-5">
      <div class="border-b border-gray-900/10 pb-12">
        <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Détails de l\'offre d'achat") }}</h3>
        <FormDateField v-model="signatureDate" field-path="attributes.signatureDate" :field-label="$gettext('Date de l\'offre')" :field-attr="signatureDateAttrs" :errors="errors" />
        <FormMoneyField v-model="price" field-path="attributes.price" :field-label="$gettext('Montant de l’offre')" :field-attrs="priceAttrs" :errors="errors" />
        <FormComboboxField v-model="notaryId" field-path="relationships.notary.data.id" :field-label="$gettext('Notaire')" :field-attrs="notaryIdAttrs" :errors="errors" :options="notaryOptions" />
        <FormCustomerComboboxField v-model="buyers" field-path="relationships.buyers.data" :field-label="$gettext('Acheteur(s)')" :field-attrs="buyersAttrs" :errors="errors"/>
        <div class="border-b border-gray-900/10 pb-12">
          <h3 class="text-base/7 font-semibold text-gray-900">{{ $gettext("Documents à importer") }}</h3>
          <FormDocumentsField
            v-model="purchaseOfferDocuments"
            category-name="signedPurchaseOffer"
            field-path="relationships.purchaseOfferDocuments.data"
            :field-label="$gettext('Offre d\'achat signée')"
            field-description=""
            :field-attrs="purchaseOfferDocumentsAttrs" :errors="errors"/>
        </div>
      </div>
    </div>
    <FormFooter @cancel="onCancel" @submit="onSubmit"/>
  </form>
</template>

<script setup>
import { useForm } from "vee-validate";
import * as yup from "yup";
import { watch, computed } from "vue";
import FormHeader from "@/components/form-fields/FormHeader.vue";
import FormFooter from "@/components/form-fields/FormFooter.vue";
import { useGettext } from "vue3-gettext";
import FormMoneyField from "@/components/form-fields/FormMoneyField.vue";
import FormCustomerComboboxField from "../form-fields/FormCustomerComboboxField.vue";
import { useNotaryStore } from "@/stores/notary";
import FormComboboxField from "../form-fields/FormComboboxField.vue";
import FormDateField from "../form-fields/FormDateField.vue";
import FormDocumentsField from "../form-fields/FormDocumentsField.vue";

const { $gettext } = useGettext();
const notaryStore = useNotaryStore();

const props = defineProps(["purchaseOffer", "title"]);
const emits = defineEmits(["submit", "cancel"]);

const validationSchema = yup.object({
  attributes: yup.object({
    price: yup.number().required($gettext("est obligatoire")),
    signatureDate: yup.date().required($gettext("est obligatoire"))
  }),
  relationships: yup.object({
    buyers: yup.object({
      data: yup.array().of(yup.object({
        id: yup.string()
      })).min(1, $gettext("au moins 1 obligatoire"))
    })
  })
});

const { errors, handleSubmit, defineField, setValues } = useForm({
  validationSchema: validationSchema,
  initialValues: props.purchaseOffer
});

const [price, priceAttrs] = defineField("attributes.price");
const [buyers, buyersAttrs] = defineField("relationships.buyers.data");
const [notaryId, notaryIdAttrs] = defineField("relationships.notary.data.id");
const [signatureDate, signatureDateAttrs] = defineField("attributes.signatureDate");
const [purchaseOfferDocuments, purchaseOfferDocumentsAttrs] = defineField("relationships.purchaseOfferDocuments.data");

const notaryOptions = computed(() =>
  notaryStore.sorted.map((notary) => ({
    value: notary.id,
    label: "(" + notary.attributes.zipcode + ") " + notary.attributes.name,
  }))
);

const stripCustomerObjects = (customers) => {
  if (!Array.isArray(customers)) return customers;
  return customers.map(({ id, type }) => ({ id, type }));
};

const onFormSubmit = ((values, actions) => {
  const payload = {
    ...values,
    relationships: {
      ...values.relationships,
      buyers: {
        ...values.relationships?.buyers,
        data: stripCustomerObjects(values.relationships?.buyers?.data)
      }
    }
  };
  emits("submit", payload, actions);
});

const onSubmit = (() => {
  handleSubmit(onFormSubmit)();
});

const onCancel = (() => {
  emits("cancel");
});

watch(() => props.purchaseOffer, (newPurchaseOffer) => {
  setValues(newPurchaseOffer);
});
</script>
